import { useInterval } from 'ahooks';
import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    DEPTH,
    HEADER_SETTINGS_DEFAULTS,
    SOCKET_SUB_TYPE,
    TRADING_TYPE,
} from 'src/common/constants';
import { getResolution, isEmptyObj, isObj } from 'src/common/helpers';
import { RiskDisclaimerModal } from 'src/components/features/derivative/modals/RishDisclaimerModal';
import { DEFAULT_FUTURE_SYMBOL } from 'src/config';
import { useAuth } from 'src/context/AuthContext';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { useKycAndFeaturesConfig } from 'src/hooks/useKycAndFeaturesConfig';
import { MasterLayout } from 'src/layout/MasterLayout';
import { APP_URLS } from 'src/setup/client/app-urls';
import {
    fetchFundingRate,
    updateFundingRate,
    saveCurrentSymbol,
    updateTvWidgetRef,
} from 'src/setup/redux/slices/derivativesSlice';
import * as socketActions from 'src/setup/redux/slices/socketSlice';
import { SOCKET_ENDPOINTS } from 'src/setup/socket/socket-endpoints';
import { FutureSymbol, SocketSubscriptionPayload } from 'src/types';

import DerivativePage from './DerivativePage';

const DerivativeWrapper: FC = () => {
    const { tradeDerivativesPlatformEnabled } = useKycAndFeaturesConfig();

    const futureSymbols = useAppSelector((state) => state.global.configV2.futuresSymbol);
    const symbol: FutureSymbol = useAppSelector((state) => state.derivatives.currentSymbol);
    const { fundingRate, tvWidgetRef } = useAppSelector((state) => state.derivatives);
    const { derivativeSettings: chartSettings, userCurrentFutureTradeAccountIndex } =
        useAppSelector((state) => state.global);
    const { userinfo, userFeaturesConfig, userVerifyInfoDirty } = useAppSelector(
        (state) => state.user,
    );

    const [isRiskDisclaimerModalOpen, setIsRiskDisclaimerModalOpen] = useState(false);

    const [socketPayload, setSocketPayload] = useState([] as Array<SocketSubscriptionPayload>);
    const [fundingRateState, setFundingRateState] = useState(fundingRate);

    const { isAuthenticated } = useAuth();

    const derivativeSettings = chartSettings || HEADER_SETTINGS_DEFAULTS;

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    useInterval(() => {
        if (
            fundingRateState &&
            Number(fundingRateState.nextSettleTime) - Number(fundingRateState.curServerTime) >= 0
        ) {
            let fixTime = 0;
            if (!fundingRateState.fixTime && fundingRateState.fixTime !== 0) {
                fixTime =
                    Number(fundingRateState.nextSettleTime) -
                    Number(fundingRateState.curServerTime);
            } else {
                fixTime = Math.max(0, fundingRateState.fixTime - 1000);
            }
            setFundingRateState({ ...fundingRateState, fixTime });
            dispatch(updateFundingRate(fundingRateState));
            if (fundingRateState.fixTime === 0) {
                dispatch(fetchFundingRate(`symbol_id=${symbol.symbolId}`));
            }
        }
    }, 1000);

    useEffect(() => {
        !isEmptyObj(userinfo) && setIsRiskDisclaimerModalOpen(!userinfo.openFuture);
    }, [userinfo]);

    useEffect(() => {
        if (
            (!userVerifyInfoDirty && !isAuthenticated) ||
            (userVerifyInfoDirty && isAuthenticated)
        ) {
            const pathArr = location.pathname.split('/');
            let symbolId = pathArr[2];

            if (userVerifyInfoDirty && isAuthenticated) {
                if (!tradeDerivativesPlatformEnabled) {
                    window.location.href = '/';
                    return;
                }
            }

            if (isObj(futureSymbols) && !isEmptyObj(futureSymbols)) {
                // Validate symbolId and navigate accordingly
                if (!Object.keys(futureSymbols).includes(symbolId)) {
                    const storedSymbol = localStorage.getItem('currentFutureSymbol');
                    symbolId = Object.keys(futureSymbols).includes(symbolId?.toUpperCase())
                        ? symbolId.toUpperCase()
                        : storedSymbol
                        ? storedSymbol
                        : Object.keys(futureSymbols).includes(DEFAULT_FUTURE_SYMBOL)
                        ? DEFAULT_FUTURE_SYMBOL
                        : Object.keys(futureSymbols)[1];

                    if (
                        userFeaturesConfig?.symbols &&
                        !userFeaturesConfig?.symbols.includes(symbolId)
                    ) {
                        window.location.href = '/';
                        return;
                    }

                    navigate(`${APP_URLS.CONTRACT}/${symbolId}`, {
                        state: location,
                        replace: true,
                    });
                    return;
                }

                if (userFeaturesConfig?.symbols && !userFeaturesConfig.symbols.includes(symbolId)) {
                    window.location.href = '/';
                    return;
                }

                dispatch(socketActions.resetTradeData());
                dispatch(saveCurrentSymbol(futureSymbols[symbolId]));
            } else {
                symbolId = symbolId ?? localStorage.getItem('currentFutureSymbol');

                if (
                    userFeaturesConfig?.symbols &&
                    !userFeaturesConfig?.symbols.includes(symbolId)
                ) {
                    window.location.href = '/';
                }
            }
        }
    }, [
        location,
        futureSymbols,
        isAuthenticated,
        userVerifyInfoDirty,
        userFeaturesConfig?.symbols,
        tradeDerivativesPlatformEnabled,
    ]);

    useEffect(() => {
        if (!isEmptyObj(symbol)) {
            dispatch(fetchFundingRate(`symbol_id=${symbol.symbolId}`));
            let aggTradeDigits = DEPTH[symbol.digitMerge.split(',')[0]];
            aggTradeDigits = aggTradeDigits <= 0 ? aggTradeDigits - 1 : aggTradeDigits;
            const interval = getResolution(derivativeSettings.chartInterval);
            const socketPayload: SocketSubscriptionPayload[] = [
                {
                    url: SOCKET_ENDPOINTS.QUOTE_V1,
                    socketType: SOCKET_SUB_TYPE.TRADE_DATA,
                    subscriptions: [
                        {
                            id: 'broker',
                            topic: 'slowBroker',
                            event: 'sub',
                            params: {
                                org: Number(symbol.orgId),
                                realtimeInterval: '24h',
                                binary: false,
                            },
                        },
                        {
                            id: `kline_${symbol.exchangeId}${symbol.symbolId}${interval}`,
                            topic: `kline_${interval}`,
                            event: 'sub',
                            symbol: `${symbol.exchangeId}.${symbol.baseTokenId}`,
                            params: { binary: true, klineType: `${interval}`, limit: 1500 },
                        },
                        {
                            id: `getSocketindexKline_${symbol.symbolId}${interval}`,
                            topic: `indexKline_${interval}`,
                            event: 'sub',
                            symbol: `${symbol.symbolName.split('-')[0]}`,
                            params: { binary: true, limit: 1500 },
                        },
                        {
                            id: `${symbol.exchangeId}.${symbol.baseTokenId}1`,
                            topic: 'mergedDepth',
                            event: 'sub',
                            symbol: `${symbol.exchangeId}.${symbol.baseTokenId}`,
                            limit: 22,
                            params: {
                                dumpScale: aggTradeDigits,
                                binary: true,
                            },
                        },
                        {
                            id: `trade${symbol.exchangeId}.${symbol.baseTokenId}`,
                            topic: 'trade',
                            event: 'sub',
                            limit: 60,
                            symbol: `${symbol.exchangeId}.${symbol.baseTokenId}`,
                            params: { org: Number(symbol.orgId), binary: true },
                        },
                    ],
                },
            ];
            if (isAuthenticated && userCurrentFutureTradeAccountIndex !== undefined) {
                socketPayload.push({
                    url: SOCKET_ENDPOINTS.USER,
                    socketType: SOCKET_SUB_TYPE.USER,
                    subscriptions: [
                        {
                            id: 'futures_order',
                            topic: 'futures_order',
                            event: 'sub',
                            accountIndex: userCurrentFutureTradeAccountIndex,
                            params: { org: Number(symbol.orgId), binary: true },
                        },
                        {
                            id: 'futures_position',
                            topic: 'futures_position',
                            event: 'sub',
                            accountIndex: userCurrentFutureTradeAccountIndex,
                            params: { org: Number(symbol.orgId), binary: true },
                        },
                        {
                            id: 'futures_tradeable',
                            topic: 'futures_tradeable',
                            event: 'sub',
                            accountIndex: userCurrentFutureTradeAccountIndex,
                        },
                    ],
                });
            }

            setSocketPayload(socketPayload);
        }
    }, [symbol, isAuthenticated, derivativeSettings, userCurrentFutureTradeAccountIndex]);

    useEffect(() => {
        setFundingRateState(fundingRate);
    }, [fundingRate]);

    useEffect(() => {
        return () => {
            if (!location.pathname.includes(APP_URLS.CONTRACT)) {
                const currentWidget = tvWidgetRef?.current._ready;
                if (currentWidget) {
                    try {
                        currentWidget?.remove();
                    } catch (error) {
                        dispatch(updateTvWidgetRef(null));
                    }
                    dispatch(updateTvWidgetRef(null));
                }
            }
        };
    }, [tvWidgetRef]);

    return (
        <MasterLayout>
            <DerivativePage payloads={socketPayload} tradeType={TRADING_TYPE.DERIVATIVES} />
            {isRiskDisclaimerModalOpen && (
                <RiskDisclaimerModal
                    isOpen={isRiskDisclaimerModalOpen}
                    handleClose={() => {
                        setIsRiskDisclaimerModalOpen(false);
                    }}
                />
            )}
        </MasterLayout>
    );
};

export default DerivativeWrapper;
